import { z } from 'zod'
import { vendorServiceSchema } from '../enums/vendor-service.enum.js'
import { emailSchema } from '../validators/email.validator.js'
import { legalAcceptanceInputSchema } from './signup.input.js'
import { passwordSchema } from './user.input.js'

/**
 * A vendor company signing itself up (docs/public-site.spec.md, "Vendor
 * signup"). Free, with no checkout: the person confirms their email instead,
 * before the company can ask venues to work with it or appear in their search.
 */

/** Where the company is based: what "venues near you" and the similar-names check are measured from. */
const companyAddress = {
  companyName: z.string().trim().min(1, 'Name your company').max(160),
  addressLine1: z.string().trim().min(1, 'Give your street address').max(200),
  city: z.string().trim().min(1, 'Give your city').max(200),
  state: z.string().trim().min(1, 'Give your state').max(200),
  zip: z.string().trim().min(1, 'Give your ZIP code').max(20),
}

export const vendorSignupInputSchema = z
  .object({
    ...companyAddress,
    addressLine2: z
      .string()
      .trim()
      .max(200)
      .transform((v) => (v === '' ? undefined : v))
      .optional(),
    services: z
      .array(vendorServiceSchema)
      .min(1, 'Choose what your company offers')
      .refine((services) => new Set(services).size === services.length, 'Choose each service once'),
    serviceRadiusMiles: z.number().int().min(1, 'Say how far you travel').max(500, 'Up to 500 miles'),
    name: z.string().trim().min(1, 'Give your name').max(160),
    email: emailSchema.max(254),
    password: passwordSchema,
    legal: legalAcceptanceInputSchema,
  })
  .strict()

export type VendorSignupInput = z.infer<typeof vendorSignupInputSchema>

/** Before creating a company: are there ones like it near this address? */
export const vendorSimilarInputSchema = z.object(companyAddress).strict()

export type VendorSimilarInput = z.infer<typeof vendorSimilarInputSchema>

/** "That's us": ask to join one of those companies. */
export const vendorJoinRequestInputSchema = z
  .object({
    vendorId: z.uuid(),
    name: z.string().trim().min(1, 'Give your name').max(160),
    email: emailSchema.max(254),
  })
  .strict()

export type VendorJoinRequestInput = z.infer<typeof vendorJoinRequestInputSchema>

/** The emailed link's token, confirming the address it was sent to. */
export const confirmEmailInputSchema = z
  .object({
    token: z.string().min(1).max(200),
  })
  .strict()

export type ConfirmEmailInput = z.infer<typeof confirmEmailInputSchema>
